import { CreateDailyNoteDto, UpdateDailyNoteDto } from '@xeetr/types/models'
import { format as formatDate } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { useEffect, useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useDailyNotes } from '@/hooks/useDailyNotes'
import { useTenant } from '@/hooks/useTenant'

interface DailyNoteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  date: Date
  onSave: (note: CreateDailyNoteDto | UpdateDailyNoteDto, id?: string) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

export function DailyNoteModal({
  open,
  onOpenChange,
  date,
  onSave,
  onDelete,
}: DailyNoteModalProps) {
  const [note, setNote] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState<Date>(date)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)

  const { tenant } = useTenant()

  useEffect(() => {
    setSelectedDate(date)
  }, [date])

  const { notes } = useDailyNotes(
    selectedDate && !isNaN(selectedDate.getTime())
      ? tenant?.timezone
        ? formatInTimeZone(selectedDate, tenant.timezone, 'yyyy-MM-dd')
        : formatDate(selectedDate, 'yyyy-MM-dd')
      : undefined,
    selectedDate && !isNaN(selectedDate.getTime())
      ? tenant?.timezone
        ? formatInTimeZone(selectedDate, tenant.timezone, 'yyyy-MM-dd')
        : formatDate(selectedDate, 'yyyy-MM-dd')
      : undefined
  )

  const selectedNote = notes.length > 0 ? notes[0] : undefined

  useEffect(() => {
    if (selectedNote) {
      setNote(selectedNote.note)
    } else {
      setNote('')
    }
  }, [selectedNote, open, selectedDate])

  const handleSave = async () => {
    if (!selectedDate || isNaN(selectedDate.getTime())) {
      console.error('Invalid selected date')
      return
    }

    setIsLoading(true)
    try {
      const formattedDate = tenant?.timezone
        ? formatInTimeZone(selectedDate, tenant.timezone, 'yyyy-MM-dd')
        : formatDate(selectedDate, 'yyyy-MM-dd')
      if (selectedNote) {
        await onSave({ note, date: formattedDate }, selectedNote.id)
      } else {
        await onSave({ date: formattedDate, note })
      }
    } catch (error) {
      console.error('Failed to save daily note:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedNote || !onDelete) return

    setIsLoading(true)
    try {
      await onDelete(selectedNote.id)
      setShowDeleteAlert(false)
    } catch (error) {
      console.error('Failed to delete daily note:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>{selectedNote ? 'Notiz bearbeiten' : 'Neue Notiz erstellen'}</DialogTitle>
          <DialogDescription>
            <div className="mb-2">
              <DatePicker
                date={selectedDate}
                onDateChange={(d) => d && setSelectedDate(d)}
                dateFormat="d. MMMM yyyy"
              />
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="note">Notiz</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Notiz für diesen Tag eingeben..."
              className="min-h-[200px]"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {selectedNote && onDelete && (
            <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="destructive"
                  disabled={isLoading}
                  loading={isLoading}
                  className="mr-auto"
                >
                  Löschen
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Notiz löschen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Diese Aktion kann nicht rückgängig gemacht werden. Die Notiz für{' '}
                    {formatDate(selectedDate, 'd. MMMM yyyy')} wird dauerhaft gelöscht.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDelete}
                    disabled={isLoading}
                    className="bg-red-600 text-white hover:bg-red-700"
                  >
                    Löschen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isLoading || !note.trim()}
            loading={isLoading}
          >
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
